import { BACKEND_URL } from '@/config/api';
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useReaderFunnel } from "../contexts/ReaderFunnelContext";
import VisitorNav from "../components/VisitorNav";
import VisitorTopMenuBar from "../components/VisitorTopMenuBar";
import VisitorFooter from "../components/VisitorFooter";
import VisitorMobileBottomNav from "../components/VisitorMobileBottomNav";
import ReaderNav from "../components/ReaderNav";
import ReaderTopMenuBar from "../components/ReaderTopMenuBar";
import ReaderMobileBottomNav from "../components/ReaderMobileBottomNav";
import UniversalFooter from "../components/UniversalFooter";
import RegistrationModal from "../components/RegistrationModal";
import ForAuthorsModal from "../components/ForAuthorsModal";
import NFMCheckoutButton from "../components/NFMCheckoutButton";
import { Check, Gift, CreditCard, BookOpen, Star, Crown, AlertCircle, Heart, Sparkles, MessageCircle, Headphones, Brain, Compass, Loader2, ShoppingCart } from "lucide-react";
import { Button } from "../components/ui/button";
import PricingConversionModal from "../components/PricingConversionModal";
import PurchaseConfirmationModal from "../components/PurchaseConfirmationModal";
import { fetchNFMProducts, NFM_PRODUCT_TYPES } from "../services/nfmService";
import { Seo, PAGE_SEO } from '../components/Seo';

// ============================================================================
// TIER ICON MAPPING
// ============================================================================
const TIER_ICONS = {
  'novice': BookOpen,
  'pro-reader': Star,
  'book-worm': Crown,
  'default': BookOpen
};

// ============================================================================
// PRICING PAGE COMPONENT
// ============================================================================
const PricingPage = () => {
  const { user, token } = useAuth();
  const { setShowPricingModal } = useReaderFunnel();
  const isAuthenticated = !!user;
  const navigate = useNavigate();
  
  // Get membership type from user object
  const membershipType = user?.membership_type || 'Free';
  // User can purchase if they are Introduction (Free) or ALaCarte
  const canPurchase = isAuthenticated && (membershipType === 'Free' || membershipType === 'ALaCarte');
  const isSubscriber = membershipType === 'Subscriber';
  
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [pricingData, setPricingData] = useState(null);
  const [nfmProducts, setNfmProducts] = useState({ subscriptions: [], bundles: [] });
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showAuthorsModal, setShowAuthorsModal] = useState(false);
  const [userState, setUserState] = useState({
    creditBalance: 0,
    creditsUsed: 0,
    subscriptionStatus: null,
    hasPaymentMethod: false,
    isIntroduction: true,
    isNewUser: true,
    isBlocked: false,
    isSubscriber: false
  });
  
  // Handle CTA actions
  const handleGetStarted = () => {
    if (isAuthenticated) {
      // User is logged in - show pricing modal
      setShowPricingModal(true);
    } else {
      // Not logged in - show registration modal
      setShowRegistrationModal(false);
      navigate('/login');
    }
  };
  
  const handleSubscriptionClick = () => {
    if (canPurchase) {
      // User can purchase subscriptions (Free or ALaCarte)
      setShowPricingModal(true);
    } else if (isAuthenticated) {
      // User is already a subscriber - go to account
      navigate('/account');
    } else {
      // Not logged in - show registration modal
      setShowRegistrationModal(true);
    }
  };
  
  const handleBundleClick = () => {
    if (isAuthenticated) {
      // Logged in - show pricing modal for bundles
      setShowPricingModal(true);
    } else {
      // Not logged in - show registration modal
      setShowRegistrationModal(true);
    }
  };

  // Fetch public pricing data (no auth required)
  useEffect(() => {
    const fetchPricingData = async () => {
      try {
        // Fetch legacy pricing data
        const response = await fetch(`${BACKEND_URL}/api/pricing/public`);
        if (response.ok) {
          const data = await response.json();
          setPricingData(data);
        }
        
        // Fetch NFM products (Phase II - Stripe-ready)
        try {
          const nfmData = await fetchNFMProducts();
          setNfmProducts(nfmData);
        } catch (nfmError) {
          console.error('Error fetching NFM products:', nfmError);
        }
      } catch (error) {
        console.error('Error fetching pricing data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPricingData();
  }, []);

  // Fetch user pricing status when authenticated
  useEffect(() => {
    const fetchPricingStatus = async () => {
      if (!isAuthenticated || !token) return;
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/reader/pricing-status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setUserState({
            creditBalance: data.credit_balance || 0,
            creditsUsed: data.credits_used || 0,
            subscriptionStatus: data.subscription_status || 'introduction',
            hasPaymentMethod: data.has_payment_method || false,
            isIntroduction: data.subscription_status === 'introduction',
            isNewUser: data.is_new_user || false,
            isBlocked: data.is_blocked || false,
            isSubscriber: data.is_subscriber || false
          });
        }
      } catch (error) {
        console.error('Error fetching pricing status:', error);
      }
    };
    
    fetchPricingStatus();
  }, [isAuthenticated, token]);

  // Get values from API or use defaults
  const introductionCredits = pricingData?.introduction_credits || 10;
  const subscriptionTiers = pricingData?.subscription_tiers || [];
  const creditBundles = pricingData?.credit_bundles 
    ? [...pricingData.credit_bundles].sort((a, b) => (a.price_usd || 0) - (b.price_usd || 0))
    : [];
  const aLaCarteTier = pricingData?.a_la_carte;

  // Determine primary CTA based on user state
  const getPrimaryCTA = () => {
    if (!isAuthenticated) {
      return { text: "Get Started Free — 10 Credits Included", action: () => setShowRegistrationModal(true), icon: Gift };
    }
    if (userState.isBlocked) {
      return { text: "Add Credits to Continue", action: () => setShowPricingModal(true), icon: CreditCard };
    }
    if (isSubscriber) {
      return { text: "Manage Subscription", action: () => navigate('/account'), icon: Star };
    }
    // For Introduction/ALaCarte users - go to My Library
    return { text: "Continue Your Journey", action: () => navigate('/reader/my-library'), icon: Sparkles };
  };

  const primaryCTA = getPrimaryCTA();

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-50 flex flex-col overflow-x-hidden">
        {isAuthenticated ? (
          <>
            <ReaderNav />
            <ReaderTopMenuBar />
          </>
        ) : (
          <>
            <VisitorNav />
            <VisitorTopMenuBar />
          </>
        )}
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-purple-500 animate-spin mx-auto mb-4" />
            <p className="text-slate-400">Loading pricing...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50 flex flex-col">
      <Seo {...PAGE_SEO.pricing} />
      {isAuthenticated ? (
        <>
          <ReaderNav />
          <ReaderTopMenuBar />
        </>
      ) : (
        <>
          <VisitorNav />
          <VisitorTopMenuBar />
        </>
      )}

      <main className="flex-1 pb-20 md:pb-0">
        {/* ================================================================ */}
        {/* HERO SECTION - Category Definition */}
        {/* ================================================================ */}
        <section className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-emerald-500 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIgb3BhY2l0eT0iMC4zIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')]"></div>
          </div>
          <div className="relative mx-auto max-w-5xl px-4 py-16 sm:py-20">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight">
              Stories Should Pull You In —<br />
              <span className="text-emerald-300">Not Make You Buy the Whole Book</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg sm:text-xl text-indigo-50 leading-relaxed">
              NewAudia unlocks <em>experiential reading</em>: interactive, immersive, and adaptive.
              You don&apos;t purchase books. You step inside them.
            </p>
            <p className="mt-4 text-base text-indigo-200/80">
              Read. Listen. Interact. Explore.<br />
              <strong className="text-white">Pay for engagement — not ownership.</strong>
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="bg-white text-purple-700 hover:bg-indigo-50 font-semibold px-8"
                onClick={primaryCTA.action}
              >
                <primaryCTA.icon className="w-5 h-5 mr-2" />
                {primaryCTA.text}
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10"
                onClick={() => document.getElementById('what-is-smartbook')?.scrollIntoView({ behavior: 'smooth' })}
              >
                What Is Experiential Reading?
              </Button>
            </div>
          </div>
        </section>

        {/* ================================================================ */}
        {/* BLOCKED STATE WARNING */}
        {/* ================================================================ */}
        {userState.isBlocked && (
          <section className="mx-auto max-w-5xl px-4 py-6">
            <div className="bg-red-900/30 border border-red-500/50 rounded-2xl p-6 flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-bold text-red-300">Your experience is paused</h3>
                <p className="mt-1 text-sm text-red-200/80">
                  Add a Credit Bundle below to continue exploring, or subscribe for ongoing access.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* ================================================================ */}
        {/* VALUE FRAMING - This Is Experiential Reading */}
        {/* ================================================================ */}
        <section className="mx-auto max-w-5xl px-4 py-10 sm:py-16 lg:py-20">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-xs sm:text-sm uppercase tracking-widest text-purple-400 font-semibold mb-3 sm:mb-4">
              A New Category of Reading
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-50">
              This Isn&apos;t Audiobooks 2.0.<br />
              <span className="text-purple-400">This Is Experiential Reading.</span>
            </h2>
            <p className="mt-4 sm:mt-6 text-sm sm:text-lg text-slate-300 leading-relaxed">
              Most platforms sell you a book and walk away.<br />
              NewAudia stays with you <em>inside</em> the story.
            </p>
          </div>
          
          <div className="mt-8 sm:mt-12 grid gap-4 sm:gap-6 md:grid-cols-3">
            <div className="text-center p-4 sm:p-6">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto rounded-xl sm:rounded-2xl bg-emerald-500/20 flex items-center justify-center mb-3 sm:mb-4">
                <Headphones className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-400" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-slate-50">Listen &amp; Read</h3>
              <p className="mt-1 sm:mt-2 text-sm text-slate-400">Professional narration with synchronized text. Follow along or lean back.</p>
            </div>
            <div className="text-center p-4 sm:p-6">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto rounded-xl sm:rounded-2xl bg-purple-500/20 flex items-center justify-center mb-3 sm:mb-4">
                <MessageCircle className="w-6 h-6 sm:w-8 sm:h-8 text-purple-400" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-slate-50">Ask Questions</h3>
              <p className="mt-1 sm:mt-2 text-sm text-slate-400">Pause and explore. Ask about themes, characters, or ideas — the book responds.</p>
            </div>
            <div className="text-center p-4 sm:p-6">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto rounded-xl sm:rounded-2xl bg-amber-500/20 flex items-center justify-center mb-3 sm:mb-4">
                <Compass className="w-6 h-6 sm:w-8 sm:h-8 text-amber-400" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-slate-50">Go Deeper</h3>
              <p className="mt-1 sm:mt-2 text-sm text-slate-400">Move non-linearly. Explore at your own pace. The experience adapts to your curiosity.</p>
            </div>
          </div>
          
          <div className="mt-8 sm:mt-12 text-center">
            <blockquote className="text-xl sm:text-2xl lg:text-3xl font-medium text-slate-200 italic">
              &ldquo;You&apos;re not consuming content.<br />
              <span className="text-emerald-400">You&apos;re participating in it.&rdquo;</span>
            </blockquote>
          </div>
        </section>

        {/* ================================================================ */}
        {/* WHAT IS AN NFD-SMARTBOOK - The Conceptual Bridge */}
        {/* ================================================================ */}
        <section id="what-is-smartbook" className="bg-slate-900/50 border-y border-slate-800">
          <div className="mx-auto max-w-5xl px-4 py-10 sm:py-16 lg:py-20">
            <div className="text-center mb-8 sm:mb-12">
              <p className="text-xs sm:text-sm uppercase tracking-widest text-emerald-400 font-semibold mb-3 sm:mb-4">
                The Technology Behind The Experience
              </p>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-slate-50">
                Books That Respond to You
              </h2>
              <p className="mt-3 sm:mt-4 text-sm sm:text-base text-slate-400 max-w-2xl mx-auto">
                NFD-SmartBooks aren&apos;t static files or one-way audio streams.
                They&apos;re structured, interactive experiences designed to respond to curiosity.
              </p>
            </div>
            
            <div className="grid gap-4 sm:gap-8 grid-cols-2 lg:grid-cols-4">
              <div className="bg-slate-900/70 border border-slate-700/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center">
                <BookOpen className="w-8 h-8 sm:w-10 sm:h-10 text-purple-400 mx-auto mb-3 sm:mb-4" />
                <h3 className="font-semibold text-slate-50 mb-1 sm:mb-2 text-sm sm:text-base">You don&apos;t just read a chapter</h3>
                <p className="text-xs sm:text-sm text-slate-400">You <em>interact</em> with it</p>
              </div>
              <div className="bg-slate-900/70 border border-slate-700/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center">
                <Headphones className="w-8 h-8 sm:w-10 sm:h-10 text-emerald-400 mx-auto mb-3 sm:mb-4" />
                <h3 className="font-semibold text-slate-50 mb-1 sm:mb-2 text-sm sm:text-base">You don&apos;t just listen</h3>
                <p className="text-xs sm:text-sm text-slate-400">You <em>engage</em></p>
              </div>
              <div className="bg-slate-900/70 border border-slate-700/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center">
                <Brain className="w-8 h-8 sm:w-10 sm:h-10 text-amber-400 mx-auto mb-3 sm:mb-4" />
                <h3 className="font-semibold text-slate-50 mb-1 sm:mb-2 text-sm sm:text-base">You don&apos;t just finish</h3>
                <p className="text-xs sm:text-sm text-slate-400">You <em>understand</em></p>
              </div>
              <div className="bg-slate-900/70 border border-slate-700/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-center">
                <Heart className="w-8 h-8 sm:w-10 sm:h-10 text-pink-400 mx-auto mb-3 sm:mb-4" />
                <h3 className="font-semibold text-slate-50 mb-1 sm:mb-2 text-sm sm:text-base">You don&apos;t just buy</h3>
                <p className="text-xs sm:text-sm text-slate-400">You <em>experience</em></p>
              </div>
            </div>
            
            <div className="mt-8 sm:mt-12 text-center">
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 max-w-2xl mx-auto">
                <p className="text-sm sm:text-lg text-slate-300 leading-relaxed">
                  NewAudia turns books into <strong className="text-white">environments</strong> —<br />
                  not products you&apos;re forced to finish to justify the price.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ================================================================ */}
        {/* HOW ACCESS WORKS - Reframed Around Depth */}
        {/* ================================================================ */}
        <section id="how-it-works" className="mx-auto max-w-5xl px-4 py-10 sm:py-16 lg:py-20">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-slate-50">
              How Deeply Do You Want to Explore?
            </h2>
            <p className="mt-2 text-sm sm:text-base text-slate-400">Credits don&apos;t unlock books. They unlock <em>depth</em>.</p>
          </div>
          
          <div className="grid gap-6 sm:gap-8 md:grid-cols-3">
            <div className="relative">
              <div className="absolute -top-3 -left-3 sm:-top-4 sm:-left-4 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-emerald-500 flex items-center justify-center text-base sm:text-lg font-bold text-white">1</div>
              <div className="bg-slate-900/70 border border-slate-700/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 pt-6 sm:pt-8">
                <h3 className="text-lg sm:text-xl font-bold text-emerald-400 mb-2 sm:mb-3">Start Free</h3>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  Step into the experience before you decide anything. <strong className="text-white">No card. No commitment. Just curiosity.</strong>
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -top-3 -left-3 sm:-top-4 sm:-left-4 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-purple-500 flex items-center justify-center text-base sm:text-lg font-bold text-white">2</div>
              <div className="bg-slate-900/70 border border-slate-700/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 pt-6 sm:pt-8">
                <h3 className="text-lg sm:text-xl font-bold text-purple-400 mb-2 sm:mb-3">Choose Your Path</h3>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  Some readers dip in. Others stay for the journey. <strong className="text-white">You decide how much access you want.</strong>
                </p>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -top-3 -left-3 sm:-top-4 sm:-left-4 w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-amber-500 flex items-center justify-center text-base sm:text-lg font-bold text-white">3</div>
              <div className="bg-slate-900/70 border border-slate-700/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 pt-6 sm:pt-8">
                <h3 className="text-lg sm:text-xl font-bold text-amber-400 mb-2 sm:mb-3">Go Deeper Anytime</h3>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  Add credits when inspiration strikes. <strong className="text-white">The more curious you are, the richer it gets.</strong>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ================================================================ */}
        {/* INTRODUCTION - Your First Experience Is On Us */}
        {/* Only show for non-authenticated users (guests/visitors) */}
        {/* ================================================================ */}
        {!isAuthenticated && (
        <section className="mx-auto max-w-5xl px-4 py-10 sm:py-16 border-t border-slate-800">
          <div className="bg-gradient-to-br from-emerald-900/40 to-teal-900/40 border border-emerald-500/30 rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:p-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 sm:gap-8">
              <div className="flex-1">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                  <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-emerald-500/30 flex items-center justify-center">
                    <Gift className="w-5 h-5 sm:w-7 sm:h-7 text-emerald-400" />
                  </div>
                  <p className="text-xs uppercase tracking-widest text-emerald-400 font-semibold">
                    Included with Every New Account
                  </p>
                </div>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-2 sm:mb-3">Your First Experience Is On Us</h3>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-3 sm:mb-4">
                  Every NewAudia account begins with <strong className="text-emerald-300">{introductionCredits} complimentary Reader Credits</strong> — enough to truly experience what makes this different.
                </p>
                <div className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-6">
                  <p className="text-sm text-slate-300 flex items-center gap-2">
                    <Headphones className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Listen to chapters with professional narration</span>
                  </p>
                  <p className="text-sm text-slate-300 flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Ask questions and have conversations with the book</span>
                  </p>
                  <p className="text-sm text-slate-300 flex items-center gap-2">
                    <Compass className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>Explore themes, characters, and ideas at your own pace</span>
                  </p>
                </div>
                <div className="bg-slate-900/50 rounded-lg sm:rounded-xl px-4 sm:px-5 py-3 sm:py-4 border border-slate-700/50 inline-block mb-4 sm:mb-6">
                  <p className="text-xs sm:text-sm text-slate-400">
                    This isn&apos;t a free trial. <strong className="text-slate-200">It&apos;s an invitation.</strong>
                  </p>
                </div>
                
                {/* GET STARTED NOW Button */}
                <div>
                  <Button 
                    size="lg"
                    className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold px-8 sm:px-12 py-4 sm:py-6 text-base sm:text-lg shadow-lg shadow-emerald-500/25"
                    onClick={() => setShowRegistrationModal(true)}
                  >
                    <Gift className="w-5 h-5 sm:w-6 sm:h-6 mr-2" />
                    GET STARTED NOW!
                  </Button>
                </div>
              </div>
              <div className="text-center md:text-right">
                <p className="text-4xl sm:text-5xl lg:text-6xl font-bold text-emerald-400">{introductionCredits}</p>
                <p className="text-base sm:text-lg text-slate-300">Reader Credits</p>
                <p className="text-xs sm:text-sm text-slate-500 mt-1 sm:mt-2">No payment required</p>
              </div>
            </div>
          </div>
        </section>
        )}

        {/* ================================================================ */}
        {/* SUBSCRIPTIONS - How Often Do You Want to Step Into These Worlds? */}
        {/* ================================================================ */}
        <section className="mx-auto max-w-5xl px-4 py-10 sm:py-16 border-t border-slate-800">
          <div className="text-center mb-6 sm:mb-10">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-slate-50">
              How Often Do You Want to Step Into These Worlds?
            </h2>
            <p className="mt-2 sm:mt-3 text-sm sm:text-base text-slate-400 max-w-2xl mx-auto">
              Subscriptions aren&apos;t about owning more books. They&apos;re about <strong className="text-slate-200">staying connected to the experience</strong>.
            </p>
            
            {/* Billing Toggle */}
            <div className="mt-6 sm:mt-8 flex items-center justify-center gap-1 sm:gap-2 bg-slate-900/70 rounded-full p-1 sm:p-1.5 border border-slate-700/50 inline-flex">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                  billingPeriod === 'monthly'
                    ? 'bg-purple-600 text-white shadow-lg'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                className={`px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all flex items-center gap-1 sm:gap-2 ${
                  billingPeriod === 'annual'
                    ? 'bg-purple-600 text-white shadow-lg'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                Annual
                <span className="text-[10px] sm:text-xs bg-emerald-500 text-white px-1.5 sm:px-2 py-0.5 rounded-full font-semibold">
                  1 Month Free
                </span>
              </button>
            </div>
          </div>

          {/* Subscription Cards - Dynamic from API */}
          <div className="grid gap-4 sm:gap-6 lg:grid-cols-3">
            {subscriptionTiers.map((tier, idx) => {
              const Icon = TIER_ICONS[tier.tier_code] || TIER_ICONS['default'];
              const price = billingPeriod === 'monthly' 
                ? tier.monthly_price_usd 
                : (tier.annual_price_usd || tier.monthly_price_usd * 11);
              const isCurrentPlan = userState.subscriptionStatus === tier.tier_code;
              const isPopular = tier.tier_code === 'pro-reader' || idx === 1;
              
              return (
                <div
                  key={tier.id || tier.tier_code}
                  className={`relative rounded-2xl sm:rounded-3xl p-5 sm:p-8 transition-all ${
                    isPopular
                      ? 'bg-gradient-to-br from-purple-900/60 to-indigo-900/60 border-2 border-purple-500/50 shadow-2xl shadow-purple-500/20 lg:scale-[1.02]'
                      : 'bg-slate-900/70 border border-slate-700/50 hover:border-slate-600'
                  }`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2">
                      <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[10px] sm:text-xs font-bold px-3 sm:px-4 py-1 sm:py-1.5 rounded-full shadow-lg whitespace-nowrap">
                        Most Popular
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center ${
                      isPopular ? 'bg-purple-500/30' : 'bg-slate-800'
                    }`}>
                      <Icon className={`w-5 h-5 sm:w-6 sm:h-6 ${isPopular ? 'text-purple-300' : 'text-slate-400'}`} />
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold text-slate-50">{tier.tier_name}</h3>
                      <p className="text-xs sm:text-sm text-slate-400">
                        {tier.tier_code === 'novice' && 'Step Into Stories Regularly'}
                        {tier.tier_code === 'pro-reader' && 'Explore Multiple Worlds'}
                        {tier.tier_code === 'book-worm' && 'Deep Immersion, Maximum Value'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mb-4 sm:mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl sm:text-5xl font-bold text-slate-50">${price}</span>
                      <span className="text-sm sm:text-base text-slate-400">/{billingPeriod === 'monthly' ? 'mo' : 'yr'}</span>
                    </div>
                    {billingPeriod === 'annual' && tier.annual_price_usd && (
                      <p className="text-xs sm:text-sm text-emerald-400 mt-1 sm:mt-2 font-medium">
                        Save ${tier.monthly_price_usd} — one month free
                      </p>
                    )}
                  </div>
                  
                  <div className="bg-slate-800/50 rounded-lg sm:rounded-xl px-4 sm:px-5 py-3 sm:py-4 mb-4 sm:mb-6">
                    <p className="text-2xl sm:text-3xl font-bold text-amber-400">
                      {tier.included_reader_credits}
                    </p>
                    <p className="text-xs sm:text-sm text-slate-400">credits every month</p>
                  </div>
                  
                  <p className="text-xs sm:text-sm text-slate-400 mb-4 sm:mb-6">
                    {tier.tier_code === 'novice' && 'Perfect for one immersive experience at a time'}
                    {tier.tier_code === 'pro-reader' && 'For readers who love variety and depth'}
                    {tier.tier_code === 'book-worm' && 'For those who live inside stories'}
                  </p>
                  
                  <Button
                    className={`w-full py-4 sm:py-6 text-sm sm:text-base font-semibold ${
                      isCurrentPlan
                        ? 'bg-slate-700 text-slate-400 cursor-default'
                        : isPopular
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white shadow-lg'
                          : 'bg-slate-700 hover:bg-slate-600 text-white'
                    }`}
                    disabled={isCurrentPlan}
                    onClick={handleSubscriptionClick}
                  >
                    {isCurrentPlan 
                      ? 'Current Plan' 
                      : canPurchase 
                        ? 'Subscribe & Start Exploring' 
                        : isSubscriber
                          ? 'Manage Subscription'
                          : 'GET STARTED NOW!'
                    }
                  </Button>
                </div>
              );
            })}
          </div>
        </section>

        {/* ================================================================ */}
        {/* A LA CARTE - Explore Without Commitment */}
        {/* ================================================================ */}
        <section className="mx-auto max-w-5xl px-4 py-10 sm:py-16 border-t border-slate-800">
          <div className="bg-slate-900/50 border border-slate-700/50 rounded-2xl sm:rounded-3xl p-5 sm:p-8 lg:p-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 sm:gap-8">
              <div className="flex-1">
                <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-1 sm:mb-2">
                  No Subscription Required
                </p>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-50 mb-2 sm:mb-3">Explore Without Commitment</h3>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-3 sm:mb-4">
                  Some readers want to wander. A La Carte lets you enter experiences when inspiration strikes — no subscription, no pressure.
                </p>
                <p className="text-sm sm:text-base text-slate-400 mb-3 sm:mb-4">
                  <strong className="text-slate-200">You&apos;re not locked into books you haven&apos;t met yet.</strong>
                </p>
                <div className="bg-amber-900/20 border border-amber-500/30 rounded-lg sm:rounded-xl px-4 sm:px-5 py-3 sm:py-4">
                  <p className="text-xs sm:text-sm text-amber-200/90">
                    <strong className="text-amber-300">Note:</strong> No monthly credits included. Reading resumes when you add a bundle.
                  </p>
                </div>
              </div>
              <div className="text-center md:text-right">
                <p className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-50">${aLaCarteTier?.monthly_price_usd || 0}</p>
                <p className="text-base sm:text-lg text-slate-400">/month</p>
                <p className="text-xs sm:text-sm text-slate-500 mt-1 sm:mt-2">{aLaCarteTier?.included_reader_credits || 0} included credits</p>
                {userState.subscriptionStatus === 'a_la_carte' && (
                  <div className="mt-3 sm:mt-4">
                    <span className="inline-block bg-slate-700 text-slate-300 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium">
                      Current Selection
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ================================================================ */}
        {/* CREDIT BUNDLES - Fuel Your Curiosity */}
        {/* ================================================================ */}
        <section id="credit-bundles" className="mx-auto max-w-5xl px-4 py-10 sm:py-16 border-t border-slate-800">
          <div className="text-center mb-6 sm:mb-10">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-slate-50">
              Fuel Your Curiosity
            </h2>
            <p className="mt-2 sm:mt-3 text-sm sm:text-base text-slate-400 max-w-2xl mx-auto">
              Credits aren&apos;t transactions. They&apos;re <em>momentum</em>. Use them to go deeper, explore longer, and ask better questions.
              {isSubscriber && <span className="text-emerald-400 font-medium"> Subscribers save on every bundle.</span>}
            </p>
            {/* Show current credit balance for authenticated users */}
            {isAuthenticated && (
              <div className="mt-3 sm:mt-4 inline-flex items-center gap-2 bg-slate-800/70 border border-slate-700 rounded-full px-3 sm:px-5 py-1.5 sm:py-2">
                <CreditCard className="w-3 h-3 sm:w-4 sm:h-4 text-amber-400" />
                <span className="text-xs sm:text-sm text-slate-300">
                  Your balance: <span className="font-semibold text-amber-400">{userState.creditBalance.toFixed(2)} credits</span>
                </span>
              </div>
            )}
          </div>

          {/* Credit Bundle Cards - Dynamic from API */}
          <div className="grid gap-3 sm:gap-5 grid-cols-2 lg:grid-cols-4">
            {creditBundles.map((bundle) => {
              const subscriberPrice = bundle.price_usd;
              const aLaCartePrice = bundle.a_la_carte_price || bundle.price_usd;
              const price = isSubscriber ? subscriberPrice : aLaCartePrice;
              const savings = aLaCartePrice - subscriberPrice;
              
              return (
                <div
                  key={bundle.id || bundle.bundle_code}
                  className="bg-slate-900/70 border border-slate-700/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:border-amber-500/50 transition-all group"
                >
                  <div className="text-center mb-3 sm:mb-4">
                    {bundle.bundle_name && (
                      <p className="text-xs sm:text-base font-semibold text-slate-200 mb-1 sm:mb-2">{bundle.bundle_name}</p>
                    )}
                    <p className="text-2xl sm:text-4xl font-bold text-amber-400 group-hover:scale-110 transition-transform">{bundle.credit_quantity}</p>
                    <p className="text-xs sm:text-sm text-slate-400">credits</p>
                  </div>
                  
                  <div className="space-y-1 sm:space-y-2 mb-3 sm:mb-5 text-xs sm:text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Subscriber</span>
                      <span className="text-emerald-400 font-semibold">${subscriberPrice.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">A La Carte</span>
                      <span className="text-slate-300">${aLaCartePrice.toFixed(2)}</span>
                    </div>
                    {isSubscriber && savings > 0 && (
                      <div className="text-[10px] sm:text-xs text-emerald-400 bg-emerald-500/10 rounded-md sm:rounded-lg px-2 sm:px-3 py-1 sm:py-1.5 text-center font-medium">
                        You save ${savings.toFixed(2)}
                      </div>
                    )}
                  </div>
                  
                  <Button
                    className={`w-full text-xs sm:text-sm py-2 sm:py-3 ${
                      userState.isBlocked
                        ? 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white'
                        : 'bg-slate-700 hover:bg-slate-600 text-white'
                    }`}
                    onClick={handleBundleClick}
                  >
                    {isAuthenticated 
                      ? (userState.isBlocked ? 'Purchase to Continue' : 'Add Credits')
                      : 'GET STARTED NOW!'
                    }
                  </Button>
                </div>
              );
            })}
          </div>
        </section>

        {/* ================================================================ */}
        {/* MANUSCRIPT UPLOADS - Author Pricing */}
        {/* ================================================================ */}
        <section id="manuscript-uploads" className="mx-auto max-w-5xl px-4 py-10 sm:py-16 border-t border-slate-800">
          <div className="text-center mb-6 sm:mb-10">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-slate-50">
              Manuscript Uploads
            </h2>
            <div className="mt-4 sm:mt-6 bg-slate-900/70 border border-slate-700/50 rounded-2xl p-6 sm:p-8 max-w-xl mx-auto">
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-slate-700/50">
                  <span className="text-slate-300">First manuscript upload</span>
                  <span className="text-emerald-400 font-bold text-lg">Free</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-slate-300">Additional manuscript uploads</span>
                  <span className="text-slate-100 font-semibold">Upload fee per manuscript</span>
                </div>
                <p className="text-amber-400/90 text-sm mt-2">(Currently $9.99 per upload)</p>
              </div>
              <p className="text-xs sm:text-sm text-slate-500 mt-4 pt-4 border-t border-slate-700/50">
                Each manuscript is processed and stored individually.
              </p>
              <Button
                onClick={() => setShowAuthorsModal(true)}
                className="mt-6 w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white py-3 text-sm sm:text-base font-semibold rounded-full"
              >
                Contact Us Directly To Upload Your First Manuscript
              </Button>
            </div>
          </div>
        </section>

        {/* ================================================================ */}
        {/* NFM CHECKOUT SECTION - Buy Now (Stripe Checkout) */}
        {/* Only shown for authenticated users - Phase II */}
        {/* Mirrors existing pricing exactly with dual bundle pricing */}
        {/* ================================================================ */}
        {isAuthenticated && (nfmProducts.subscription_tiers?.length > 0 || nfmProducts.bundles?.length > 0) && (
        <section id="nfm-checkout" className="mx-auto max-w-5xl px-4 py-10 sm:py-16 border-t border-slate-800">
          <div className="text-center mb-6 sm:mb-10">
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full px-4 py-1.5 mb-4">
              <ShoppingCart className="w-4 h-4 text-emerald-400" />
              <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wide">Secure Checkout</span>
            </div>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight text-slate-50">
              Buy Now with Stripe
            </h2>
            <p className="mt-2 sm:mt-3 text-sm sm:text-base text-slate-400 max-w-2xl mx-auto">
              Complete your purchase securely with Stripe. Credits are added immediately after payment.
            </p>
          </div>

          {/* NFM Subscription Tiers - Show for non-subscribers */}
          {!isSubscriber && nfmProducts.subscription_tiers?.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-slate-200 mb-4">Subscription Plans</h3>
              
              {/* Billing Toggle */}
              <div className="flex items-center justify-center gap-2 mb-6">
                <button
                  onClick={() => setBillingPeriod('monthly')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    billingPeriod === 'monthly'
                      ? 'bg-purple-600 text-white'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => setBillingPeriod('annual')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                    billingPeriod === 'annual'
                      ? 'bg-purple-600 text-white'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Annual
                  <span className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded-full">Save</span>
                </button>
              </div>
              
              <div className="grid gap-4 sm:gap-6 lg:grid-cols-3">
                {nfmProducts.subscription_tiers.map((tier, idx) => {
                  const price = billingPeriod === 'monthly' ? tier.monthly_price_cents : tier.annual_price_cents;
                  // POLICY: Always show monthly credits (credits issued monthly, regardless of billing cadence)
                  const monthlyCredits = tier.monthly_credits;
                  const planId = `${tier.tier_code.replace('-', '_')}_${billingPeriod}`;
                  const isPopular = idx === 1; // Middle tier is popular
                  
                  return (
                    <div
                      key={tier.tier_code}
                      className={`relative rounded-2xl p-5 sm:p-6 ${
                        isPopular
                          ? 'bg-gradient-to-br from-purple-900/60 to-indigo-900/60 border-2 border-purple-500/50'
                          : 'bg-slate-900/70 border border-slate-700/50'
                      }`}
                    >
                      {isPopular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <span className="bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                            Most Popular
                          </span>
                        </div>
                      )}
                      {billingPeriod === 'annual' && tier.annual_savings_cents > 0 && (
                        <div className="absolute -top-3 right-4">
                          <span className="bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                            Save ${(tier.annual_savings_cents / 100).toFixed(0)}
                          </span>
                        </div>
                      )}
                      <div className="mb-4">
                        <h4 className="text-lg font-bold text-white">{tier.tier_name}</h4>
                        {/* POLICY: Always show monthly credits */}
                        <p className="text-sm text-slate-400">{monthlyCredits.toLocaleString()} credits/month</p>
                        {billingPeriod === 'annual' && (
                          <p className="text-xs text-slate-500 mt-1">Billed annually • Credits issued monthly</p>
                        )}
                      </div>
                      <div className="mb-4">
                        <p className="text-3xl font-bold text-white">${(price / 100).toFixed(0)}</p>
                        <p className="text-xs text-slate-400">/{billingPeriod === 'annual' ? 'year' : 'month'}</p>
                      </div>
                      <NFMCheckoutButton
                        productType={NFM_PRODUCT_TYPES.SUBSCRIPTION}
                        productId={planId}
                        className={`w-full ${
                          isPopular
                            ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500'
                            : 'bg-slate-700 hover:bg-slate-600'
                        }`}
                      >
                        Subscribe Now
                      </NFMCheckoutButton>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* NFM Credit Bundle Cards - Dual Pricing */}
          {nfmProducts.bundles?.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-slate-200 mb-2">Credit Bundles</h3>
              <p className="text-sm text-slate-400 mb-4">
                {isSubscriber 
                  ? <span className="text-emerald-400">Subscriber pricing applied</span>
                  : 'One-time purchase • A La Carte pricing'
                }
              </p>
              <div className="grid gap-3 sm:gap-5 grid-cols-2 lg:grid-cols-4">
                {nfmProducts.bundles.map((bundle) => {
                  const price = isSubscriber ? bundle.subscriber_price_cents : bundle.alacarte_price_cents;
                  const savings = bundle.alacarte_price_cents - bundle.subscriber_price_cents;
                  
                  return (
                    <div
                      key={bundle.bundle_id}
                      className="bg-slate-900/70 border border-slate-700/50 rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:border-emerald-500/50 transition-all group"
                    >
                      <div className="text-center mb-2">
                        <p className="text-xs text-slate-500">{bundle.name}</p>
                      </div>
                      <div className="text-center mb-3 sm:mb-4">
                        <p className="text-2xl sm:text-3xl font-bold text-amber-400 group-hover:scale-110 transition-transform">
                          {bundle.credits}
                        </p>
                        <p className="text-xs sm:text-sm text-slate-400">credits</p>
                      </div>
                      <div className="text-center mb-2">
                        <p className="text-xl font-bold text-white">${(price / 100).toFixed(2)}</p>
                        {!isSubscriber && savings > 0 && (
                          <p className="text-xs text-slate-500">
                            Subscribers: ${(bundle.subscriber_price_cents / 100).toFixed(2)}
                          </p>
                        )}
                      </div>
                      <NFMCheckoutButton
                        productType={NFM_PRODUCT_TYPES.BUNDLE}
                        productId={bundle.bundle_id}
                        isSubscriber={isSubscriber}
                        className="w-full bg-emerald-600 hover:bg-emerald-500 text-white text-xs sm:text-sm"
                      >
                        Buy Now
                      </NFMCheckoutButton>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
        )}

        {/* ================================================================ */}
        {/* CLOSING - This Isn't About Books */}
        {/* ================================================================ */}
        <section className="bg-gradient-to-br from-slate-900 to-slate-950 border-t border-slate-800">
          <div className="mx-auto max-w-4xl px-4 py-12 sm:py-20 text-center">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-50 mb-4 sm:mb-6">
              This Isn&apos;t About Books
            </h2>
            <p className="text-lg sm:text-xl lg:text-2xl text-slate-300 mb-6 sm:mb-8">
              It&apos;s About <span className="text-emerald-400 font-semibold">How Reading Should Feel</span>
            </p>
            
            <div className="grid gap-4 sm:gap-6 md:grid-cols-3 mb-8 sm:mb-12">
              <div className="bg-slate-800/50 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                <Check className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-400 mx-auto mb-2 sm:mb-3" />
                <p className="text-sm sm:text-base text-slate-300">You shouldn&apos;t have to buy a whole book to know if it&apos;s worth your time</p>
              </div>
              <div className="bg-slate-800/50 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                <Check className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-400 mx-auto mb-2 sm:mb-3" />
                <p className="text-sm sm:text-base text-slate-300">You shouldn&apos;t lose money for stopping when curiosity is satisfied</p>
              </div>
              <div className="bg-slate-800/50 rounded-xl sm:rounded-2xl p-4 sm:p-6">
                <Check className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-400 mx-auto mb-2 sm:mb-3" />
                <p className="text-sm sm:text-base text-slate-300">You should decide how deep you want to go</p>
              </div>
            </div>
            
            <p className="text-sm sm:text-lg text-slate-400 mb-3 sm:mb-4">
              NewAudia wasn&apos;t built to sell books.
            </p>
            <p className="text-base sm:text-xl text-slate-200 mb-6 sm:mb-8 font-medium">
              It was built to restore the joy of reading.
            </p>
            
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold px-6 sm:px-10 py-4 sm:py-6 text-sm sm:text-lg"
              onClick={isAuthenticated ? (userState.isBlocked ? () => setShowPricingModal(true) : () => navigate('/reader/my-library')) : () => setShowRegistrationModal(true)}
            >
              {isAuthenticated 
                ? (userState.isBlocked ? "Add Credits Now" : "Continue Your Journey") 
                : "GET STARTED NOW!"
              }
            </Button>
          </div>
        </section>

        {/* FAQ Link */}
        <section className="mx-auto max-w-5xl px-4 py-6 sm:py-10 text-center">
          <p className="text-sm sm:text-base text-slate-400">
            Have questions?{' '}
            <Link to="/faq" className="text-purple-400 hover:text-purple-300 underline underline-offset-4">
              Visit our FAQ
            </Link>
          </p>
        </section>
      </main>

      {isAuthenticated ? (
        <>
          <ReaderMobileBottomNav />
          <UniversalFooter />
        </>
      ) : (
        <>
          <VisitorMobileBottomNav />
          <VisitorFooter />
        </>
      )}
      
      {/* Pricing Modals */}
      <PricingConversionModal />
      <PurchaseConfirmationModal />

      {/* Registration Modal */}
      <RegistrationModal
        isOpen={showRegistrationModal}
        onClose={() => setShowRegistrationModal(false)}
      />

      {/* For Authors Modal */}
      <ForAuthorsModal
        isOpen={showAuthorsModal}
        onClose={() => setShowAuthorsModal(false)}
        onGetStarted={() => navigate('/contact')}
      />
    </div>
  );
};

export default PricingPage;
